<!--
  ExtractedDataEditor — shared editor for extracted document data fields.

  Bug fixed here: the old inline {#each} blocks used `bind:value={obj[key]}`
  which mutates the object in-place. Svelte's reactive system only reacts to
  *assignment*, not property-level mutation, so `isDirty` in the parent never
  fired and `saveIfDirty()` discarded the changes.

  Fix: every field change dispatches a `change` event with a **freshly
  assigned** Record (spread operator). The parent reassigns its local variable
  (`editableExtractedValues = e.detail`), which triggers Svelte reactivity and
  makes `isDirty` fire correctly.

  Props:
    values   — current flat string map (reactive; parent owns the variable)
    readonly — when true, all fields render as static text (no inputs)

  Events:
    change   — CustomEvent<Record<string,string>> — complete, newly-assigned
                copy of the field map after one field changed. Parent must:
                  on:change={(e) => editableExtractedValues = e.detail}
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { _ } from 'svelte-i18n';
  import DateInput from './DateInput.svelte';
  import {
    fieldLabel,
    fieldType,
    fieldI18nKey,
    parseAmount,
    CORE_FIELD_ORDER,
    HIDDEN_FIELDS,
  } from '../../extractedFields';

  /** The current flat extracted-values map. Parent owns the variable. */
  export let values: Record<string, string> = {};
  /** When true every field is non-editable (for display-only contexts). */
  export let readonly = false;

  const dispatch = createEventDispatcher<{ change: Record<string, string> }>();

  function handleInputChange(key: string, e: Event) {
    const input = e.target as HTMLInputElement;
    handleChange(key, input.value);
  }

  // -------------------------------------------------------------------------
  // Field ordering & grouping
  // -------------------------------------------------------------------------

  /** Core fields present in `values`, in canonical order. */
  $: coreFields = CORE_FIELD_ORDER.filter((k) => k in values);

  /** Non-core, non-hidden fields in original key order. */
  $: otherFields = Object.keys(values).filter(
    (k) => !HIDDEN_FIELDS.has(k) && !(CORE_FIELD_ORDER as readonly string[]).includes(k),
  );

  $: hasOtherFields = otherFields.length > 0;

  // -------------------------------------------------------------------------
  // Change handler — creates a new object so parent reactivity fires
  // -------------------------------------------------------------------------
  function handleChange(key: string, newValue: string) {
    dispatch('change', { ...values, [key]: newValue });
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  function label(key: string): string {
    return fieldLabel(key, $_);
  }

  function rawKey(key: string): string {
    return key;
  }
</script>

<div class="space-y-4">
  <!-- Core fields -->
  {#if coreFields.length > 0}
    <div>
      <p class="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-2">
        {$_('extracted_fields.section_core')}
      </p>
      <div class="space-y-2">
        {#each coreFields as key (key)}
          {@const type = fieldType(key)}
          {@const currentValue = values[key] ?? ''}
          <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3 items-center">
            <!-- Label -->
            <span
              class="text-xs text-text-muted text-right truncate leading-tight"
              title={rawKey(key)}
            >
              {label(key)}
            </span>

            <!-- Input widget -->
            {#if type === 'amount'}
              <!-- system:amount is read-only: the structured "value=X,currency=Y,period=Z"
                   format would be corrupted by free-text editing. A dedicated amount
                   editor with separate value/currency/period inputs is a future enhancement. -->
              {@const parsed = parseAmount(currentValue)}
              <span
                class="text-xs text-text bg-bg-alt px-2 py-1 rounded border border-border/50 select-all"
                title={currentValue}
              >
                {#if parsed}
                  {parsed.value}{parsed.currency ? ' ' + parsed.currency : ''}{parsed.period &&
                  parsed.period !== 'once'
                    ? ' (' + parsed.period + ')'
                    : parsed.period === 'once'
                      ? ' ' + $_('extracted_fields.amount_once')
                      : ''}
                {:else}
                  {currentValue || '—'}
                {/if}
              </span>
            {:else if type === 'date'}
              {#if readonly}
                <span class="text-xs text-text">{currentValue || '—'}</span>
              {:else}
                <DateInput value={currentValue} on:change={(e) => handleChange(key, e.detail)} />
              {/if}
            {:else if readonly}
              <span class="text-xs text-text">{currentValue || '—'}</span>
            {:else}
              <input
                type="text"
                value={currentValue}
                on:change={(e) => handleInputChange(key, e)}
                class="px-2 py-1 bg-bg-alt border border-border rounded text-xs text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
              />
            {/if}
          </div>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Other fields -->
  {#if hasOtherFields}
    <div>
      <p class="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-2">
        {$_('extracted_fields.section_other')}
      </p>
      <div class="space-y-2">
        {#each otherFields as key (key)}
          {@const type = fieldType(key)}
          {@const currentValue = values[key] ?? ''}
          <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3 items-center">
            <span
              class="text-xs text-text-muted text-right truncate leading-tight"
              title={rawKey(key)}
            >
              {label(key)}
            </span>

            {#if type === 'date'}
              {#if readonly}
                <span class="text-xs text-text">{currentValue || '—'}</span>
              {:else}
                <DateInput value={currentValue} on:change={(e) => handleChange(key, e.detail)} />
              {/if}
            {:else if readonly}
              <span class="text-xs text-text">{currentValue || '—'}</span>
            {:else}
              <input
                type="text"
                value={currentValue}
                on:change={(e) => handleInputChange(key, e)}
                class="px-2 py-1 bg-bg-alt border border-border rounded text-xs text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
              />
            {/if}
          </div>
        {/each}
      </div>
    </div>
  {/if}
</div>
